<template>
  <SfModalPortal :modal-id="modalId" size="s" @opened="onOpen">
    <template #title>
      <div class="sf-level">
        {{ title }}
        <i
          v-if="tooltip"
          v-tooltip.text="tooltip"
          class="fa fa-info-circle sf-pointer sf-c-text-light"
        />
      </div>
    </template>
    <template #messages>
      <div v-if="hasErrors" class="tw-bg-sf-danger-subtle-2 tw-px-8 tw-py-4">
        {{ I18n.t('pulse.sharing.error_wf') }}
      </div>
      <div v-if="isEmpty" class="tw-bg-sf-warning-subtle-2 tw-px-8 tw-py-4">
        {{ I18n.t('pulse.sharing.no_pulse_users') }}
      </div>
      <div v-if="periodPrivate" class="tw-bg-yellow-50 tw-px-8 tw-py-4">
        {{ I18n.t('pulse.sharing.will_make_period_public') }}
      </div>
    </template>
    <template #body>
      <div
        class="
          tw-flex tw-min-h-[300px] tw-flex-col tw-overflow-y-auto
          pulse-share-scroll-area
        "
      >
        <div v-if="isLoadingData">
          <div class="sf-level sf-level-around sf-pad-2">
            <span class="sf-spinner sf-inline-block" />
          </div>
        </div>
        <div v-else class="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
          <div
            v-if="hasUsers"
            class="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col"
            :class="hideSessionClass"
          >
            <div v-if="anyUsersShared" class="tw-shrink-0">
              <div
                v-dompurify-html="sharedMessage"
                class="tw-pb-4 tw-font-medium"
              />
            </div>
            <div
              v-else
              class="tw-shrink-0 tw-pb-4 tw-font-medium tw-text-sf-gray-darker"
            >
              {{ unsharedMessage }}
            </div>
            <!-- Only the user list scrolls, so the message above and the
            "allow proposing changes" controls below stay visible. -->
            <div
              class="
                tw-min-h-0
                tw-overflow-y-auto
                tw-rounded-lg
                tw-bg-sf-neutral-subtle-2
                tw-py-0.5
              "
            >
              <div>
                <div v-for="(user, index) in effectiveUsers" :key="user.email">
                  <PulseUserRow
                    :user="user"
                    :is-disabled="user.isDisabled"
                    :last="index === users.length - 1"
                    :active-share-type-dropdown-user-id="
                      activeShareTypeDropdownUserId
                    "
                    @update-user="onUpdateUser"
                    @toggle-share-type-dropdown="onToggleShareTypeDropown"
                  ></PulseUserRow>
                </div>
              </div>
            </div>
            <div class="tw-shrink-0">
              <ShareWithPulseUsersAdditionalInformation
                :ledger-id="ledgerId"
                :shared-object-id="sharedObjectId"
                :selected-users-count="sharedUsers.length"
                :active-proposals="activeProposals"
                :allow-changes="allowChanges"
                :show-textarea="showTextarea"
                @toggle-allow-changes="handleToggleChanges"
                @update-message="handleUpdateMessage"
              >
              </ShareWithPulseUsersAdditionalInformation>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="tw-flex tw-justify-center tw-gap-2">
        <SfButtonDs :disabled="isLoadingData" @click="closeModal">
          {{ I18n.t('common.cancel') }}
        </SfButtonDs>
        <SfButtonDs
          v-if="hasUsers"
          variant="primary"
          :disabled="isLoadingData"
          @click="handleSaveClick"
        >
          {{
            showTextarea
              ? I18n.t('shared.save_and_send')
              : I18n.t('shared.save')
          }}
        </SfButtonDs>
      </div>
    </template>
  </SfModalPortal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { Ref } from 'vue'

import { SfButtonDs } from '@silverfin/ui-components'
import { SfModalPortal } from '@app/shared/vue/components/sf-modal'
import PulseUserRow from './pulse-user-row.vue'
import ShareWithPulseUsersAdditionalInformation from './share-with-pulse-users-additional-information.vue'
import { type PulseShareUser, type PulseShareResponseData, type Proposal, type PulseShareType, type UserUpdatePayload, type UserLocalUpdatePayload, pulseShareTypes } from '../../shared/types/pulse-share'
import { sharingApi } from '@app/shared/api'
import { usePulseShare } from '@app/shared/pinia/share-pulse'
import getPiniaInstance from '@app/shared/pinia/instance'
import type { ModalPortalPayloadMap } from '@app/types'
import useRedactSessionRecording from '@app/shared/vue/composables/use-hide-session-recording'

const I18n = window.I18n

export interface ModalProps {
  ledgerName: string
  ledgerId: number
  sharedObjectId: number
  title: string
  tooltip?: string | null
  isBlockedInitial?: boolean
  sharedObjectClass?: string
  shareType?: PulseShareType | null
  periodPrivate: boolean
}

interface Props extends ModalProps {
  unsharedMessage: string
  sharedMessage: (usersCount: number) => string
  modalId: keyof ModalPortalPayloadMap
}

const props = withDefaults(defineProps<Props>(), {
  tooltip: null,
  sharedObjectClass: '',
  isBlockedInitial: false,
  shareType: null,
})

const hideSessionClass = useRedactSessionRecording()

const message = ref('')

const activeShareTypeDropdownUserId = ref(0)
const users: Ref<Array<PulseShareUser>> = ref([])
const isLoadingData = ref(false)
const persistedSharedUserIds: Ref<Array<number>> = ref([]) // users with access at the moment of opening the modal/copied after save
const allowChanges = ref(!props.isBlockedInitial)
const activeProposals: Ref<Proposal[]> = ref([])
const errors: Ref<Array<string>> = ref([])

const pulseShareStore = usePulseShare(getPiniaInstance())

const showTextarea = computed(() => {
  return userIdsToMessage.value.length > 0
})
const sharedUsers = computed(() => {
  return users.value.filter(user => user.shared)
})

const userIdsToMessage = computed(() => {
  return sharedUsers.value.filter((user) => !persistedSharedUserIds.value.includes(user.id))
})

const anyUsersShared = computed(() => {
  return sharedUsers.value.length > 0
})

const hasErrors = computed(() => {
  return errors.value.length > 0
})

const hasUsers = computed(() => {
  return users.value.length > 0
})

const isEmpty = computed(() => {
  return !isLoadingData.value && !hasUsers.value
})

const effectiveUsers = computed(() => {
  return users.value.map((user, index) => ({
    ...user,
    isDisabled: isCheckboxDisabled(user),
    last: index === users.value.length - 1,
  }))
})

const onOpen = () => {
  activeShareTypeDropdownUserId.value = 0
  getData()
}

async function handleSaveClick() {
  isLoadingData.value = true
  errors.value = []
  try {
    const usersPayload = sharedUsers.value.map((user) => ({shareType: user.shareType, id: user.id} as UserUpdatePayload))

    const data = await sharingApi.shareWithPulseUsers({
      ledgerId: props.ledgerId,
      sharedObjectId: props.sharedObjectId,
      body: {
        users: usersPayload,
        message: showTextarea.value ? message.value : null,
        blockProposals: !allowChanges.value,
      },
    })
    updateStateFromApiResponse(data)
    pulseShareStore.setCurrentObjectBlocked(!allowChanges.value)
    pulseShareStore.refreshPulseSharesData(props.ledgerId, props.sharedObjectId)
  } catch (e) {
    if (e instanceof Error) {
      errors.value.push(e.message)
    }
  }
  if (!hasErrors.value) {
    closeModal()
    window.eventHub.$emit('refreshCommunicationData')
  }
  if (props.periodPrivate && anyUsersShared.value) {
    // the period is set to public, reload to put changes in effect
    window.Turbolinks.visit(window.location.href)
  }
}

async function getData() {
  isLoadingData.value = true

  try {
    const [data, proposalData] = await Promise.all([
      sharingApi.getPulseShareData({
        ledgerId: props.ledgerId,
        sharedObjectId: props.sharedObjectId
      }),

      props.sharedObjectClass === 'CompanyWorkflow'
        ? await sharingApi.getWorkflowProposalData({
            ledgerId: props.ledgerId,
            workflowId: props.sharedObjectId,
          })
        : Promise.resolve([]),
    ])

    if (proposalData) {
      activeProposals.value = proposalData
    }

    updateStateFromApiResponse(data)
  } catch (e) {
    if (e instanceof Error) {
      errors.value.push(e.message.toString())
    }
  }
}

function updateStateFromApiResponse(data: PulseShareResponseData | null) {
  isLoadingData.value = false

  if (data === null) {
    return
  }

  users.value = (data.users ?? []) as Array<PulseShareUser>
  persistedSharedUserIds.value = sharedUsers.value.map(user => user.id)
}

function closeModal() {
  window.modalCitizen.closeModalPortal()
}

function isCheckboxDisabled(user: PulseShareUser): boolean {
  return (
    props.sharedObjectClass === 'ReconciliationText' &&
    persistedSharedUserIds.value.includes(user.id)
  )
}

function onUpdateUser(payload: UserLocalUpdatePayload, userId: number) {
  const user = users.value.find((user) => user.id === userId)
  if (user) {
    Object.assign(user, payload)

    if (user.shared && !user.shareType) {
      user.shareType = pulseShareTypes.PREPARE
    }
  }
}

function handleToggleChanges(toggled: boolean) {
  allowChanges.value = toggled
}

const sharedMessage = computed(() =>
  props.sharedMessage(sharedUsers.value.length)
)

function handleUpdateMessage(msg: string | undefined) {
  message.value = msg ? msg : ''
}

function onToggleShareTypeDropown(id: number) {
  activeShareTypeDropdownUserId.value = id
}
</script>

<style scoped lang="scss">
.pulse-share-scroll-area {
  max-height: min(600px, theme('maxHeight.modal-body-height'));
}
</style>
